import type { ReactNode } from "react";
import classNames from "classnames";
import { SwitchIndicator } from "@design-system/Switch/SwitchIndicator";
import { CheckIcon, CopyIcon, EditIcon, TrashIcon, TriangleIcon } from "@icons";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import menuStyles from "./ComponentAnatomySchematicMenu.module.scss";
import styles from "./ComponentAnatomyView.module.scss";

type MenuBindingReader = ReturnType<typeof bindingReader>;

const textStyle = (
    read: MenuBindingReader,
    partId: "itemLabel" | "sectionTitle",
) => ({
    fontFamily: read(partId, "font-family"),
    fontSize: read(partId, "font-size"),
    fontWeight: read(partId, "font-weight"),
    lineHeight: read(partId, "line-height"),
    letterSpacing: read(partId, "letter-spacing"),
});

const stateRowLabels: Readonly<Record<string, string>> = {
    triangle: "Move to",
    switch: "Show voided",
    check: "Compact",
};

/** The state row's trailing glyph, from the variant's `indicator` bindings. */
const MenuSchematicIndicator = ({
    read,
    highlight,
    isDisabled,
}: {
    read: MenuBindingReader;
    highlight: AnatomyPartHighlight;
    isDisabled: boolean;
}) => {
    const glyph = read("indicator", "glyph");
    if (glyph === undefined) {
        return null;
    }

    return (
        <Part
            partId="indicator"
            highlight={highlight}
            className={menuStyles.menuIndicator}
            style={{
                width: read("indicator", "width"),
                height: read("indicator", "height"),
                color: read("indicator", "color"),
            }}
        >
            {glyph === "triangle" && (
                <TriangleIcon className={menuStyles.menuTriangle} />
            )}
            {glyph === "check" && <CheckIcon />}
            {glyph === "switch" && (
                <SwitchIndicator checked disabled={isDisabled} />
            )}
        </Part>
    );
};

/** One row on the plate; only the state row takes the resolved colours and indicator. */
const MenuSchematicRow = ({
    read,
    highlight,
    label,
    icon,
    isStateRow,
    isDestructive = false,
}: {
    read: MenuBindingReader;
    highlight: AnatomyPartHighlight;
    label: string;
    icon: ReactNode;
    isStateRow: boolean;
    isDestructive?: boolean;
}) => {
    const restingColor = isDestructive
        ? "var(--fg-error)"
        : "var(--fg-primary)";
    const color = isStateRow ? read("itemLabel", "color") : restingColor;
    const indicatorGlyph = isStateRow ? read("indicator", "glyph") : undefined;
    // Toggle and radio rows carry no leading icon, as on the live stage.
    const hasLeadingGlyph =
        indicatorGlyph !== "switch" && indicatorGlyph !== "check";

    return (
        <Part
            partId="itemBackground"
            highlight={highlight}
            className={menuStyles.menuRow}
            style={{
                minHeight: read("itemBackground", "min-height"),
                paddingInline: read("itemBackground", "padding-inline"),
                gap: read("itemBackground", "gap"),
                borderRadius: read("itemBackground", "border-radius"),
                backgroundColor: isStateRow
                    ? read("itemBackground", "background-color")
                    : "transparent",
            }}
        >
            {hasLeadingGlyph && (
                <Part
                    partId="glyph"
                    highlight={highlight}
                    className={menuStyles.menuGlyph}
                    style={{
                        width: read("glyph", "width"),
                        height: read("glyph", "height"),
                        color: isStateRow ? read("glyph", "fill") : color,
                    }}
                >
                    {icon}
                </Part>
            )}
            <Part
                partId="itemLabel"
                highlight={highlight}
                className={menuStyles.menuRowLabel}
                style={{ color, ...textStyle(read, "itemLabel") }}
            >
                {label}
            </Part>
            {isStateRow && (
                <MenuSchematicIndicator
                    read={read}
                    highlight={highlight}
                    isDisabled={color === "var(--fg-disabled)"}
                />
            )}
        </Part>
    );
};

/**
 * X-ray plate for Menu: an open popup with a group label, rows and a
 * separator; the first row ends in the variant's trailing indicator.
 */
export const MenuSchematic = ({
    bindings,
    highlight,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
}) => {
    const read = bindingReader(bindings);
    const rowProps = { read, highlight };

    return (
        <span
            className={classNames(styles.schematic, menuStyles.menuSchematic)}
        >
            <Part
                partId="surface"
                highlight={highlight}
                className={menuStyles.menuSurface}
                style={{
                    backgroundColor: read("surface", "background-color"),
                    borderColor: read("surface", "border-color"),
                    borderWidth: read("surface", "border-width"),
                    borderRadius: read("surface", "border-radius"),
                    boxShadow: read("surface", "box-shadow"),
                    padding: read("surface", "padding"),
                    rowGap: read("surface", "row-gap"),
                }}
            >
                <Part
                    partId="sectionTitle"
                    highlight={highlight}
                    className={menuStyles.menuGroupLabel}
                    style={{
                        color: read("sectionTitle", "color"),
                        padding: read("sectionTitle", "padding"),
                        ...textStyle(read, "sectionTitle"),
                    }}
                >
                    Invoice
                </Part>
                <MenuSchematicRow
                    {...rowProps}
                    label={
                        stateRowLabels[read("indicator", "glyph") ?? ""] ??
                        "Edit"
                    }
                    icon={<EditIcon />}
                    isStateRow
                />
                <MenuSchematicRow
                    {...rowProps}
                    label="Duplicate"
                    icon={<CopyIcon />}
                    isStateRow={false}
                />
                <Part
                    partId="separator"
                    highlight={highlight}
                    className={menuStyles.menuSeparator}
                    style={{
                        height: read("separator", "height"),
                        backgroundColor: read("separator", "background-color"),
                        margin: read("separator", "margin"),
                    }}
                />
                <MenuSchematicRow
                    {...rowProps}
                    label="Delete"
                    icon={<TrashIcon />}
                    isStateRow={false}
                    isDestructive
                />
            </Part>
        </span>
    );
};
